import { Button, CountrySelectList, WidgetInput, WidgetDropDownObjects, } from '@yellowcard/components-library-vue3';
import { computed, defineComponent, nextTick, onMounted, ref, watch, } from 'vue';
import { useKycStore } from '@/store/modules/kyc';
import { useAuthStore } from '@/store/modules/auth';
import { storeToRefs } from 'pinia';
import { KycTypes } from '@/types/kyc';
import validator from 'validator';
import { CountryTypes } from '@/types/country';
import { GeneralData } from '@/utils/general';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
import { nameIsInvalid, validateMobileNumber } from '@/utils/helpers';
import useTransactionsStore from '@/store/modules/transactions';
export default defineComponent({
    name: 'PersonalDetails',
    props: {},
    components: {
        Button,
        CountrySelectList,
        WidgetInput,
        WidgetDropDownObjects,
    },
    setup() {
        const kycStore = useKycStore();
        const { currentStep, personalDetails: userInput, kycResetKey, statesCities, statesRefreshKey, } = storeToRefs(kycStore);
        const transactionsStore = useTransactionsStore();
        const { countryConfig, countryConfigs, activeSupportedCountryConfig } = storeToRefs(transactionsStore);
        const { countryCode, country } = countryConfig.value;
        const authStore = useAuthStore();
        const { partnerId } = storeToRefs(authStore);
        const ageError = ref(false);
        const isButtonDisabled = ref(true);
        const defaultCountryCodeConfig = {
            value: countryCode,
            label: countryCode,
            country,
        };
        const rules = computed(() => ({
            // remove error messages while confirming correct error state
            firstNames: (value) => {
                if (validator.isEmpty(value) || nameIsInvalid(value))
                    return ' ';
                return true;
            },
            lastName: (value) => {
                if (validator.isEmpty(value) || nameIsInvalid(value))
                    return ' ';
                return true;
            },
            day: (value) => {
                if (validator.isEmpty(value))
                    return ' ';
                if (!/^\d+$/.test(value))
                    return ' ';
                if (value.length > 2)
                    return ' ';
                const day = Number(value);
                const year = Number(userInput.value.year);
                const month = Number(userInput.value.month);
                if (day < 1 || day > 31)
                    return ' ';
                if (year) {
                    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
                    if (isLeapYear && month === 2)
                        if (day < 1 || day > 29)
                            return ' ';
                }
                if (month) {
                    const daysInMonth = GeneralData.Months.map(val => val.days);
                    if (day < 1 || day > daysInMonth[month - 1])
                        return ' ';
                }
                return true;
            },
            month: (value) => {
                if (validator.isEmpty(value))
                    return ' ';
                return true;
            },
            year: (value) => {
                if (validator.isEmpty(value))
                    return ' ';
                if (!/^\d+$/.test(value))
                    return ' ';
                if (value.length !== 4)
                    return ' ';
                if (Number(value) < 1900)
                    return ' ';
                const year = new Date().getFullYear();
                if (year - Number(value) < 18)
                    return ' ';
                return true;
            },
            mobileNumber: (value) => {
                if (validator.isEmpty(value))
                    return ' ';
                return validateMobileNumber(value, activeSupportedCountryConfig.value?.phoneValidation.MIN_PHONE_LENGTH, activeSupportedCountryConfig.value?.phoneValidation.MAX_PHONE_LENGTH);
            },
        }));
        function validateDob(inputs, value) {
            if (!inputs.year || !inputs.month || !inputs.day)
                return true;
            const VALID_AGE = 18;
            const valid = /\d{4}-\d{2}-\d{2}/.test(value);
            if (!valid)
                return ' ';
            const [dobYear, dobMonth, dobDay] = value.split('-');
            const year = new Date().getFullYear();
            const age = year - Number(dobYear);
            if (age > VALID_AGE)
                return true;
            if (age < VALID_AGE)
                return ' ';
            // age == VALID_AGE
            const month = new Date().getMonth() + 1;
            if (month > Number(dobMonth))
                return true;
            if (month < Number(dobMonth))
                return ' ';
            const day = new Date().getDate();
            if (day < Number(dobDay))
                return ' ';
            return true;
        }
        function validateInputs(newInput) {
            const dob = `${newInput.year}-${newInput.month}-${newInput.day.toString().padStart(2, '0')}`;
            const validDob = validateDob(newInput, dob) === true;
            ageError.value = !validDob;
            const validFields = !(rules.value.firstNames(newInput.firstNames) === true &&
                rules.value.lastName(newInput.lastName) === true &&
                rules.value.day(newInput.day) === true &&
                rules.value.month(newInput.month) === true &&
                rules.value.year(newInput.year) === true &&
                rules.value.mobileNumber(newInput.mobileNumber) === true &&
                validDob);
            return validFields;
        }
        const backClick = () => {
            currentStep.value = KycTypes.KycSteps.KYC_1;
        };
        onMounted(() => {
            isButtonDisabled.value = validateInputs(userInput.value);
        });
        watch(userInput.value, newInput => {
            nextTick(() => {
                isButtonDisabled.value = validateInputs(newInput);
            });
        }, { deep: true });
        const proceed = () => {
            const { firstNames, lastName, year, month, day, mobileNumber } = userInput.value;
            const dob = `${year}-${month}-${day.toString().padStart(2, '0')}`;
            const phone = `${countryConfig.value.countryCode}${mobileNumber}`;
            userInput.value.countryCode = countryConfig.value.countryCode;
            trackEvent(MIXPANEL_EVENTS.KYC.PERSONAL_DETAILS, {
                partner: partnerId.value,
            });
            authStore.setPersonalDetails(firstNames, lastName, dob, phone);
            currentStep.value = KycTypes.KycSteps.KYC_3;
        };
        const countrySelect = (value) => {
            const config = transactionsStore.countryConfigs.find(item => item.countryCode === value);
            transactionsStore.setCountryConfig({ countryCode: config.country });
            statesCities.value.splice(0);
            authStore
                .GetCountryStates()
                // eslint-disable-next-line
                .then(_ => (statesRefreshKey.value += 1))
                .catch(e => {
                console.error('failed to get city states', e);
            });
            userInput.value.countryCode = value;
        };
        function getCountry() {
            return countryConfigs.value.map(config => ({
                value: config.countryCode,
                label: config.countryCode,
                country: config.country,
            }));
        }
        const monthSelect = (value) => {
            const [selectedMonth] = GeneralData.Months.filter(month => month.value === value);
            userInput.value.month = selectedMonth.label;
        };
        const monthInput = (num) => {
            const [selectedMonth] = GeneralData.Months.filter(value => value.label === num);
            return selectedMonth;
        };
        const onKeyPress = () => {
            console.log('key pressed');
        };
        return {
            backClick,
            userInput,
            rules,
            proceed,
            isButtonDisabled,
            onKeyPress,
            countrySelect,
            getCountry,
            monthSelect,
            monthInput,
            CountryTypes,
            GeneralData,
            kycResetKey,
            ageError,
            validateInputs,
            defaultCountryCodeConfig,
        };
    },
});
